import { useState } from 'react'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import {
  useManagerPlayerContributionDetail,
  useManagerPlayerContributions,
  useManagerPlayerRoundBreakdown,
  useManagerLeaguePlayerContribution,
  useManagerUserPlayerContributionDetail,
  useManagerUserPlayerContributions,
  type ManagerPlayerContributionRound,
  type ManagerPlayerContributionRoundPlayer,
  type ManagerPlayerContributionTotal,
} from '@/features/manager/api'
import { useTranslation } from '@/i18n'

type ContributionsView = 'all' | 'player' | 'breakdown' | 'league-player' | 'league-breakdown'
type ContributionsTab = 'total' | 'rounds'

export function ManagerPlayerContributionsModal({
  leagueId,
  targetUserId,
  targetUserName,
  initialPlayerId,
  initialPlayerName,
  onClose,
}: {
  leagueId: number
  targetUserId?: number | undefined
  targetUserName?: string | undefined
  initialPlayerId?: number | undefined
  initialPlayerName?: string | undefined
  onClose: () => void
}) {
  const { t, locale } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)
  const [view, setView] = useState<ContributionsView>(initialPlayerId != null ? 'league-player' : 'all')
  const [tab, setTab] = useState<ContributionsTab>('total')
  const [selectedPlayerId, setSelectedPlayerId] = useState<number>()
  const [selectedRoundId, setSelectedRoundId] = useState<number>()
  const [selectedLeagueRoundId, setSelectedLeagueRoundId] = useState<number>()
  const ownContributionsQuery = useManagerPlayerContributions(
    leagueId,
    targetUserId == null && initialPlayerId == null,
  )
  const memberContributionsQuery = useManagerUserPlayerContributions(leagueId, targetUserId, targetUserId != null)
  const contributionsQuery = targetUserId == null ? ownContributionsQuery : memberContributionsQuery
  const ownPlayerDetailQuery = useManagerPlayerContributionDetail(
    leagueId,
    selectedPlayerId,
    view === 'player' && targetUserId == null,
  )
  const memberPlayerDetailQuery = useManagerUserPlayerContributionDetail(
    leagueId,
    targetUserId,
    selectedPlayerId,
    view === 'player' && targetUserId != null,
  )
  const playerDetailQuery = targetUserId == null ? ownPlayerDetailQuery : memberPlayerDetailQuery
  const breakdownQuery = useManagerPlayerRoundBreakdown(
    leagueId,
    selectedRoundId,
    selectedPlayerId,
    view === 'breakdown',
  )
  const leaguePlayerQuery = useManagerLeaguePlayerContribution(
    leagueId,
    initialPlayerId,
    view === 'league-player' || view === 'league-breakdown',
  )

  function requestClose() {
    if (isClosing) return
    setIsClosing(true)
    window.setTimeout(onClose, 280)
  }

  function goBack() {
    if (view === 'breakdown') {
      setView('player')
      setSelectedRoundId(undefined)
      return
    }

    if (view === 'league-breakdown') {
      setView('league-player')
      setSelectedLeagueRoundId(undefined)
      return
    }

    if (view === 'league-player') {
      requestClose()
      return
    }

    if (view === 'player') {
      setView('all')
      setSelectedPlayerId(undefined)
    }
  }

  function openPlayer(playerId: number) {
    setSelectedPlayerId(playerId)
    setView('player')
  }

  function openRound(roundId: number) {
    setSelectedRoundId(roundId)
    setView('breakdown')
  }

  function openLeagueRound(roundId: number) {
    setSelectedLeagueRoundId(roundId)
    setView('league-breakdown')
  }

  const selectedLeagueRound = leaguePlayerQuery.data?.rounds.find((round) => round.app_round.id === selectedLeagueRoundId)
  const title = view === 'breakdown'
    ? breakdownQuery.data?.app_round.name || t('Puntenopbouw')
    : view === 'league-breakdown'
      ? selectedLeagueRound?.app_round.name || t('Puntenopbouw')
    : view === 'player'
      ? playerDetailQuery.data?.player.name || t('Speler details')
      : view === 'league-player'
        ? leaguePlayerQuery.data?.player.name || initialPlayerName || t('Speler details')
      : targetUserName
        ? t('Punten van {name}', { name: targetUserName })
        : t('Bekijk alle spelers')

  return (
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        aria-label={t('Sluiten')}
        onClick={requestClose}
        className={`${isClosing ? 'animate-drawer-backdrop-out' : 'animate-drawer-backdrop-in'} absolute inset-0 bg-black/80 backdrop-blur-sm`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-player-contributions-title"
        className={`${isClosing ? 'animate-manager-player-picker-out' : 'animate-manager-player-picker-in'} absolute inset-x-0 bottom-0 mx-auto flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]`}
      >
        <header className="flex items-start gap-3 border-b border-white/[0.07] p-5 sm:p-6">
          {view !== 'all' ? (
            <button
              type="button"
              onClick={goBack}
              className="mt-0.5 inline-flex h-9 shrink-0 items-center rounded-lg px-2 text-sm font-bold text-surface-400 transition hover:bg-white/[0.06] hover:text-white"
            >
              <span aria-hidden="true" className="mr-1 text-lg">←</span>
              {t('Terug')}
            </button>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Golero Manager')}</p>
            <h2 id="manager-player-contributions-title" className="mt-1 truncate text-xl font-black tracking-tight text-white sm:text-2xl">
              {title}
            </h2>
          </div>
          <ModalCloseButton onClick={requestClose} label={t('Sluiten')} className="h-9 w-9" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          {view === 'all' ? (
            <AllPlayersView
              query={contributionsQuery}
              tab={tab}
              onTabChange={setTab}
              onPlayerTap={openPlayer}
            />
          ) : null}
          {view === 'player' ? (
            <PlayerContributionView
              query={playerDetailQuery}
              onRoundTap={openRound}
            />
          ) : null}
          {view === 'breakdown' ? (
            <PlayerBreakdownView query={breakdownQuery} locale={locale} />
          ) : null}
          {view === 'league-player' ? (
            <LeaguePlayerContributionView query={leaguePlayerQuery} onRoundTap={openLeagueRound} />
          ) : null}
          {view === 'league-breakdown' ? (
            <LeaguePlayerRoundBreakdownView
              query={leaguePlayerQuery}
              roundId={selectedLeagueRoundId}
              locale={locale}
            />
          ) : null}
        </div>
      </section>
    </div>
  )
}

function AllPlayersView({
  query,
  tab,
  onTabChange,
  onPlayerTap,
}: {
  query: ReturnType<typeof useManagerPlayerContributions>
  tab: ContributionsTab
  onTabChange: (tab: ContributionsTab) => void
  onPlayerTap: (playerId: number) => void
}) {
  const { t } = useTranslation()
  const totals = query.data?.totals.players ?? []
  const rounds = query.data?.rounds ?? []

  if (query.isLoading) return <LoadingState />

  if (query.isError) {
    return (
      <StateCard
        title={t('Spelerpunten niet geladen')}
        body={query.error instanceof Error ? query.error.message : t('De punten per speler konden niet worden geladen.')}
        action={t('Opnieuw proberen')}
        onAction={() => void query.refetch()}
      />
    )
  }

  if (totals.length === 0 && rounds.length === 0) {
    return <StateCard title={t('Nog geen spelers met punten')} body={t('Zodra er spelerpunten zijn in deze league, zie je ze hier terug.')} />
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 rounded-xl border border-white/[0.08] bg-[#111719] p-1">
        {([
          ['total', t('Totaal')],
          ['rounds', t('Per speelronde')],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => onTabChange(value)}
            className={[
              'rounded-lg px-3 py-2.5 text-sm font-bold transition',
              tab === value ? 'bg-white/[0.1] text-white' : 'text-surface-500 hover:text-surface-200',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'total' ? (
        <div className="space-y-2">
          {totals.map((player) => (
            <ContributionPlayerRow
              key={player.player_id}
              player={player}
              points={player.total_points}
              subtitle={t('{count} rondes • beste ronde {points} pt', {
                count: player.rounds_count,
                points: player.best_round_points,
              })}
              onClick={() => onPlayerTap(player.player_id)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          {rounds.map((round) => (
            <section key={round.app_round.id} className="rounded-2xl border border-white/[0.07] bg-[#111719] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-black text-white">{round.app_round.name}</h3>
                  <p className="mt-1 text-xs text-surface-500">{round.players.length} {t('spelers')} · {round.total_points} {t('punten')}</p>
                </div>
                <span className="text-sm font-black text-accent-200">{round.total_points} {t('pnt')}</span>
              </div>
              <div className="space-y-2">
                {round.players.map((player) => (
                  <ContributionPlayerRow
                    key={`${round.app_round.id}-${player.player_id}`}
                    player={player}
                    points={player.points}
                    subtitle={player.slot || player.position_group || undefined}
                    onClick={() => onPlayerTap(player.player_id)}
                    compact
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function PlayerContributionView({
  query,
  onRoundTap,
}: {
  query: ReturnType<typeof useManagerPlayerContributionDetail>
  onRoundTap: (roundId: number) => void
}) {
  const { t } = useTranslation()
  const data = query.data

  if (query.isLoading) return <LoadingState />
  if (query.isError || !data) {
    return (
      <StateCard
        title={t('Spelerbijdrage niet geladen')}
        body={query.error instanceof Error ? query.error.message : t('De spelerbijdrage kon niet worden geladen.')}
        action={t('Opnieuw proberen')}
        onAction={() => void query.refetch()}
      />
    )
  }

  const player = data.player
  return (
    <div className="space-y-5">
      <PlayerHero player={player} points={player.total_points} />
      {data.rounds.length === 0 ? (
        <StateCard title={t('Nog geen speelrondes')} body={t('Deze speler heeft nog geen punten in een speelronde behaald.')} />
      ) : (
        <div className="space-y-2">
          {data.rounds.map((round) => (
            <button
              key={round.app_round.id}
              type="button"
              onClick={() => {
                if (round.app_round.id !== undefined) onRoundTap(round.app_round.id)
              }}
              className="flex w-full items-center gap-3 rounded-2xl border border-white/[0.07] bg-[#111719] p-4 text-left transition hover:border-accent-300/30 hover:bg-[#151d1f]"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{round.app_round.name}</p>
                <p className="mt-1 text-xs text-surface-500">
                  {[round.slot, t('Speelronde {sequence}', { sequence: round.app_round.sequence ?? 0 })].filter(Boolean).join(' • ')}
                </p>
              </div>
              <span className="shrink-0 text-sm font-black text-white">{round.points} {t('pnt')}</span>
              <span aria-hidden="true" className="text-lg text-surface-500">→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function LeaguePlayerContributionView({
  query,
  onRoundTap,
}: {
  query: ReturnType<typeof useManagerLeaguePlayerContribution>
  onRoundTap: (roundId: number) => void
}) {
  const { t } = useTranslation()
  const data = query.data

  if (query.isLoading) return <LoadingState />
  if (query.isError || !data) {
    return (
      <StateCard
        title={t('Puntenopbouw niet geladen')}
        body={query.error instanceof Error ? query.error.message : t('De puntenopbouw kon niet worden geladen.')}
        action={t('Opnieuw proberen')}
        onAction={() => void query.refetch()}
      />
    )
  }

  return (
    <div className="space-y-5">
      <PlayerHero player={data.player} points={data.player.total_points} />
      {data.rounds.length === 0 ? (
        <StateCard title={t('Nog geen speelrondes')} body={t('Voor deze speler is nog geen puntenopbouw per speelronde beschikbaar.')} />
      ) : (
        <div className="space-y-2">
          {data.rounds.map((round) => (
            <button
              key={round.app_round.id}
              type="button"
              onClick={() => {
                if (round.app_round.id !== undefined) onRoundTap(round.app_round.id)
              }}
              className="flex w-full items-center gap-3 rounded-2xl border border-white/[0.07] bg-[#111719] p-4 text-left transition hover:border-accent-300/30 hover:bg-[#151d1f]"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{round.app_round.name}</p>
                <p className="mt-1 text-xs text-surface-500">
                  {round.matches.length} {t('wedstrijden')} · {t('Punten per wedstrijd')}
                </p>
              </div>
              <span className="shrink-0 text-sm font-black text-white">{round.round_points} {t('pnt')}</span>
              <span aria-hidden="true" className="text-lg text-surface-500">→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function LeaguePlayerRoundBreakdownView({
  query,
  roundId,
  locale,
}: {
  query: ReturnType<typeof useManagerLeaguePlayerContribution>
  roundId: number | undefined
  locale: string
}) {
  const { t } = useTranslation()
  const data = query.data
  const round = data?.rounds.find((item) => item.app_round.id === roundId)

  if (query.isLoading) return <LoadingState />
  if (query.isError || !data || !round) {
    return (
      <StateCard
        title={t('Puntenopbouw niet geladen')}
        body={query.error instanceof Error ? query.error.message : t('De puntenopbouw kon niet worden geladen.')}
        action={t('Opnieuw proberen')}
        onAction={() => void query.refetch()}
      />
    )
  }

  return (
    <div className="space-y-5">
      <PlayerHero player={data.player} points={round.round_points} subtitle={round.app_round.name} />
      {round.matches.length === 0 ? (
        <StateCard title={t('Geen wedstrijden gevonden')} body={t('Voor deze speelronde zijn geen wedstrijden met puntenverdeling gevonden.')} />
      ) : (
        <div className="space-y-3">
          {round.matches.map((match) => (
            <BreakdownMatchCard key={match.match.id} match={match} locale={locale} />
          ))}
        </div>
      )}
    </div>
  )
}

function PlayerBreakdownView({
  query,
  locale,
}: {
  query: ReturnType<typeof useManagerPlayerRoundBreakdown>
  locale: string
}) {
  const { t } = useTranslation()
  const data = query.data

  if (query.isLoading) return <LoadingState />
  if (query.isError || !data) {
    return (
      <StateCard
        title={t('Puntenopbouw niet geladen')}
        body={query.error instanceof Error ? query.error.message : t('De puntenopbouw kon niet worden geladen.')}
        action={t('Opnieuw proberen')}
        onAction={() => void query.refetch()}
      />
    )
  }

  return (
    <div className="space-y-5">
      <PlayerHero player={data.player} points={data.player.round_points} subtitle={data.app_round.name} />
      {data.matches.length === 0 ? (
        <StateCard title={t('Geen wedstrijden gevonden')} body={t('Voor deze speelronde zijn geen wedstrijden met puntenverdeling gevonden.')} />
      ) : (
        <div className="space-y-3">
          {data.matches.map((match) => (
            <BreakdownMatchCard key={match.match.id} match={match} locale={locale} />
          ))}
        </div>
      )}
    </div>
  )
}

function ContributionPlayerRow({
  player,
  points,
  subtitle,
  onClick,
  compact = false,
}: {
  player: ManagerPlayerContributionTotal | ManagerPlayerContributionRoundPlayer
  points: number
  subtitle?: string | undefined
  onClick: () => void
  compact?: boolean
}) {
  const { t } = useTranslation()
  const team = player.team_display_name || player.team_name
  const meta = [team, player.position_group, player.jersey_number ? `#${player.jersey_number}` : null].filter(Boolean).join(' • ')

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0d1113] p-3 text-left transition hover:border-accent-300/30 hover:bg-[#151d1f]"
    >
      <PlayerAvatar src={player.image_path} name={player.name} compact={compact} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-white">{player.name || t('Speler')}</p>
        {meta ? <p className="mt-1 truncate text-xs text-surface-500">{meta}</p> : null}
        {subtitle ? <p className="mt-1 truncate text-xs text-surface-600">{subtitle}</p> : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-base font-black text-white">{points}</p>
        <p className="text-[10px] uppercase tracking-[0.12em] text-surface-600">{t('pnt')}</p>
      </div>
      <span aria-hidden="true" className="text-lg text-surface-500">→</span>
    </button>
  )
}

function PlayerHero({
  player,
  points,
  subtitle,
}: {
  player: {
    name: string
    image_path?: string | null | undefined
    team_name?: string | null | undefined
    team_display_name?: string | null | undefined
    position_group?: string | null | undefined
    jersey_number?: number | null | undefined
  }
  points: number
  subtitle?: string | undefined
}) {
  const { t } = useTranslation()
  const team = player.team_display_name || player.team_name
  const meta = [team, player.position_group, player.jersey_number ? `#${player.jersey_number}` : null].filter(Boolean).join(' • ')

  return (
    <section className="flex items-center gap-4 rounded-2xl border border-accent-300/15 bg-accent-300/[0.06] p-4">
      <PlayerAvatar src={player.image_path} name={player.name} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-black text-white">{player.name || t('Speler')}</p>
        {meta ? <p className="mt-1 truncate text-xs text-surface-400">{meta}</p> : null}
        {subtitle ? <p className="mt-1 truncate text-xs text-surface-500">{subtitle}</p> : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-2xl font-black text-white">{points}</p>
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-surface-500">{t('punten')}</p>
      </div>
    </section>
  )
}

function BreakdownMatchCard({
  match,
  locale,
}: {
  match: NonNullable<ReturnType<typeof useManagerPlayerRoundBreakdown>['data']>['matches'][number]
  locale: string
}) {
  const { t } = useTranslation()
  const home = match.match.home_team.display_name || match.match.home_team.name || match.match.home_team.short_name || t('Onbekend')
  const away = match.match.away_team.display_name || match.match.away_team.name || match.match.away_team.short_name || t('Onbekend')
  const kickoffLabel = formatKickoff(match.match.kickoff, locale)
  const status = normalizeMatchStatus(match.match.status)

  return (
    <section className="rounded-2xl border border-white/[0.07] bg-[#111719] p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-white">{home} - {away}</p>
          {kickoffLabel ? <p className="mt-1 text-xs text-surface-500">{kickoffLabel}</p> : null}
        </div>
        {status ? <span className="shrink-0 rounded-full bg-white/[0.08] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-surface-300">{status === 'final' ? t('Definitief') : status === 'live' ? t('Live') : t('Gesloten')}</span> : null}
      </div>
      <div className="my-3 border-t border-white/[0.07]" />
      <div className="space-y-1">
        <StatRow label={t('Gespeelde minuten')} count={match.breakdown.minutes_played} points={match.breakdown.minutes_points} alwaysShow />
        <StatRow label={t('Doelpunten')} count={match.breakdown.goals} points={match.breakdown.goal_points} />
        <StatRow label={t('Assists')} count={match.breakdown.assists} points={match.breakdown.assist_points} />
        <StatRow label={t('Gele kaarten')} count={match.breakdown.yellow_cards} points={match.breakdown.yellow_card_points} />
        <StatRow label={t('Rode kaarten')} count={match.breakdown.red_cards} points={match.breakdown.red_card_points} />
        <StatRow label={t('Eigen doelpunten')} count={match.breakdown.own_goals} points={match.breakdown.own_goal_points} />
        {match.breakdown.clean_sheet || match.breakdown.clean_sheet_points !== 0 ? (
          <StatRow label={t('Clean sheet')} points={match.breakdown.clean_sheet_points} />
        ) : null}
        <StatRow label={t('Reddingen')} count={match.breakdown.saves} points={match.breakdown.saves_points} />
        <StatRow label={t('Penaltyreddingen')} count={match.breakdown.penalties_saved} points={match.breakdown.penalties_saved_points} />
        <StatRow label={t('Key passes')} count={match.breakdown.key_passes} points={match.breakdown.key_passes_points} />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-white/[0.07] pt-3">
        <span className="text-sm font-bold text-white">{t('Totaal van deze wedstrijd')}</span>
        <span className="manager-match-total-pill rounded-full bg-accent-300/15 px-3 py-1 text-sm font-black text-accent-100">{match.breakdown.total} {t('pnt')}</span>
      </div>
    </section>
  )
}

function StatRow({
  label,
  count,
  points,
  alwaysShow = false,
}: {
  label: string
  count?: number
  points: number
  alwaysShow?: boolean
}) {
  const { t } = useTranslation()
  if (!alwaysShow && (count ?? 0) <= 0 && points === 0) return null

  const value = count === undefined ? label : `${count}× ${label}`
  const pointLabel = points >= 0 ? `+${points}` : String(points)

  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="text-surface-300">{value}</span>
      <span className="shrink-0 font-bold text-surface-100">{pointLabel} {t('pnt')}</span>
    </div>
  )
}

function PlayerAvatar({
  src,
  name,
  compact = false,
}: {
  src: string | null | undefined
  name: string | null | undefined
  compact?: boolean
}) {
  const size = compact ? 'h-9 w-9 text-xs' : 'h-12 w-12 text-sm'
  return src ? (
    <img src={src} alt="" className={`${size} shrink-0 rounded-full bg-[#172024] object-cover`} />
  ) : (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-[#172024] font-bold text-accent-200`}>
      {(name || 'S').slice(0, 1).toUpperCase()}
    </span>
  )
}

function StateCard({
  title,
  body,
  action,
  onAction,
}: {
  title: string
  body: string
  action?: string
  onAction?: () => void
}) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-[#111719] p-6 text-center">
      <p className="text-sm font-bold text-white">{title}</p>
      <p className="mt-2 text-sm leading-6 text-surface-400">{body}</p>
      {action && onAction ? (
        <button type="button" onClick={onAction} className="mt-4 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">
          {action}
        </button>
      ) : null}
    </div>
  )
}

function LoadingState() {
  return <div className="flex min-h-56 items-center justify-center"><div className="h-1.5 w-40 animate-pulse rounded-full bg-accent-400/70" /></div>
}

function formatKickoff(value: string | null | undefined, locale: string) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function normalizeMatchStatus(status: string | null | undefined): 'final' | 'live' | 'closed' | null {
  switch (status?.trim().toLowerCase()) {
    case 'ft':
    case 'finished':
      return 'final'
    case 'live':
    case 'ht':
      return 'live'
    case 'cancelled':
    case 'postponed':
      return 'closed'
    default:
      return null
  }
}
